import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminAPI } from "../../api/AdminEndpoints";
import LogsView from "./logs/LogsView";
import PerformanceView from "./performance/PerformanceView";
import { buildStyles } from "./styles";
import useLocalStorageState from "./useLocalStorageState";
import { FALLBACK_SERVICES, clamp, formatTime, useDebouncedValue } from "./utils";
import "./styles.css";
import "../dashboard/components/Marker-UI.css";
import PageTitle from '../../components/PageTitle';

/**
 * Extract only "free text" parts for backend contains filter.
 * Tokens like level:, action:, user:, host: stay frontend-only.
 * Supports msg:foo => sends "foo" to backend.
 */
function extractContains(q) {
  const tokens = (q || "").trim().split(/\s+/).filter(Boolean);

  const free = tokens
    .filter((t) => {
      const m = t.match(/^(\w+):/);
      if (!m) return true;
      const key = m[1].toLowerCase();
      return !["level", "action", "user", "host"].includes(key);
    })
    .map((t) => {
      const m = t.match(/^msg:(.+)$/i);
      return m ? m[1] : t;
    });

  return free.join(" ");
}

export default function MonitoringPage() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(() => {
    const v = searchParams.get("view");
    return v === "performance" ? "performance" : "logs";
  });

  // sync view when URL param changes (nav dropdown click)
  useEffect(() => {
    const v = searchParams.get("view");
    if (v === "performance" || v === "logs") setView(v);
  }, [searchParams]);

  // shared refresh controls
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshSec, setRefreshSec] = useState(5);
  const timerRef = useRef(null);

  // ---- LOGS ----
  const [service, setService] = useState("backend");
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [servicesLoading, setServicesLoading] = useState(true);

  const [limit, setLimit] = useState(200);
  const [minutes, setMinutes] = useState(60);

  const [query, setQuery] = useState("");

  // ✅ Backend gets only free-text contains (NOT tokens like level:warn)
  const containsQuery = useMemo(() => extractContains(query), [query]);
  const debouncedContains = useDebouncedValue(containsQuery, 250);

  const [rows, setRows] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsErr, setLogsErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [logsUpdatedAt, setLogsUpdatedAt] = useState(null);

  // ---- METRICS ----
  const HISTORY_MAX = 120; // max points in sparkline
  const HISTORY_TTL_MS = 24 * 60 * 60 * 1000; // 24h

  const [windowMinutes, setWindowMinutes] = useState(5);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsErr, setMetricsErr] = useState("");
  const [metricsUpdatedAt, setMetricsUpdatedAt] = useState(null);

  const [metricsHistory, setMetricsHistory] = useLocalStorageState(
    "clubradar.metricsHistory",
    [],
    { ttlMs: HISTORY_TTL_MS }
  );

  // Reset selection when leaving logs (UX)
  useEffect(() => {
    if (view !== "logs") setSelected(null);
  }, [view]);

  // Load services once
  useEffect(() => {
    let alive = true;

    (async () => {
      setServicesLoading(true);
      try {
        const res = await AdminAPI.getLogServices();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!alive) return;

        if (list.length > 0) {
          setServices(list);
          setService((prev) => (list.includes(prev) ? prev : list[0]));
        } else {
          setServices(FALLBACK_SERVICES);
        }
      } catch {
        if (!alive) return;
        setServices(FALLBACK_SERVICES);
      } finally {
        if (alive) setServicesLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // ✅ Refs halten IMMER die neuesten Parameter (für Auto-Refresh)
  const logsParamsRef = useRef({ service, limit, minutes, contains: debouncedContains });
  useEffect(() => {
    logsParamsRef.current = { service, limit, minutes, contains: debouncedContains };
  }, [service, limit, minutes, debouncedContains]);

  const metricsParamsRef = useRef({ windowMinutes });
  useEffect(() => {
    metricsParamsRef.current = { windowMinutes };
  }, [windowMinutes]);

  // ✅ Loader lesen aus den Refs (kein stale closure mehr)
  const loadLogs = useCallback(async () => {
    const { service, limit, minutes, contains } = logsParamsRef.current;

    setLogsLoading(true);
    setLogsErr("");

    try {
      const data = await AdminAPI.getLogs({
        service,
        limit,
        minutes,
        contains,
      });

      const sorted = [...(data || [])].sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
      setRows(sorted);
      setLogsUpdatedAt(new Date());
    } catch (e) {
      const backendMsg =
        typeof e?.response?.data === "string" ? e.response.data : e?.response?.data?.message;

      setLogsErr(backendMsg || e?.message || String(e));
      setRows([]);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    const { windowMinutes } = metricsParamsRef.current;

    setMetricsLoading(true);
    setMetricsErr("");

    try {
      const data = await AdminAPI.getMetricsOverview({ windowMinutes });

      setMetrics(data || null);
      setMetricsUpdatedAt(new Date());

      // ✅ push history point (for sparklines)
      if (data) {
        const cpu = Number(data?.cpu?.process ?? 0);

        const used = Number(data?.jvm?.usedBytes ?? 0);
        const max = Number(data?.jvm?.maxBytes ?? 0);
        const memRatio = max > 0 ? used / max : 0;

        const dbActive = Number(data?.dbPool?.active ?? 0);
        const dbMax = Number(data?.dbPool?.max ?? 0);
        const dbRatio = dbMax > 0 ? dbActive / dbMax : 0;

        const snap = {
          t: Date.now(),
          cpu,
          memRatio,
          dbRatio,
          p95Ms: Number(data?.latency?.p95Ms ?? 0),
          rps: Number(data?.requests?.approxRps ?? 0),
        };

        setMetricsHistory((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          const next = [...arr, snap];
          return next.slice(-HISTORY_MAX);
        });
      }
    } catch (e) {
      const backendMsg =
        typeof e?.response?.data === "string" ? e.response.data : e?.response?.data?.message;

      setMetricsErr(backendMsg || e?.message || String(e));
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [setMetricsHistory]);

  // Load when relevant changes happen
  useEffect(() => {
    if (view !== "logs") return;
    loadLogs();
  }, [view, service, limit, minutes, debouncedContains, loadLogs]);

  useEffect(() => {
    if (view !== "performance") return;
    loadMetrics();
  }, [view, windowMinutes, loadMetrics]);

  // ✅ Auto-refresh active view
  useEffect(() => {
    if (!autoRefresh) return;

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      if (view === "logs") loadLogs();
      if (view === "performance") loadMetrics();
    }, clamp(refreshSec, 2, 60) * 1000);

    return () => timerRef.current && clearInterval(timerRef.current);
  }, [autoRefresh, refreshSec, view, loadLogs, loadMetrics]);

  // Status pill
  const perfStatus = metrics?.health?.status || (metricsErr ? "bad" : "ok");
  const logsStatus = logsErr ? "bad" : "ok";
  const activeStatus = view === "performance" ? perfStatus : logsStatus;

  const styles = buildStyles({ selected });

  const headerHint = useMemo(() => {
    if (view === "logs") {
      if (!logsUpdatedAt) return "—";
      return `${formatTime(logsUpdatedAt)}${logsLoading ? " (lädt …)" : ""}`;
    }
    if (!metricsUpdatedAt) return "—";
    return `${formatTime(metricsUpdatedAt)}${metricsLoading ? " (lädt …)" : ""}`;
  }, [view, logsUpdatedAt, logsLoading, metricsUpdatedAt, metricsLoading]);

  const pill = styles.pill(activeStatus);

  return (
    <div className="page">
      <div className="page-header">
        <PageTitle title="Monitoring" description="Der technische Zustand von Schick: Logs und Performance der Server. Vor allem für die Fehlersuche." />

        {/* Logs / Performance sub-tab selector */}
        <div style={styles.tabRow}>
          <button style={styles.tab(view === "logs")} onClick={() => setView("logs")}>
            Logs
          </button>
          <button style={styles.tab(view === "performance")} onClick={() => setView("performance")}>
            Performance
          </button>
        </div>
      </div>

      {/* Secondary bar: subtitle + status + refresh */}
      <div style={styles.topBar}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <p style={{ ...styles.subtitle, margin: 0 }}>
            Zuletzt aktualisiert: <b>{headerHint}</b>
          </p>
          <div style={pill.style}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: pill.dot }} />
            {activeStatus === "ok" ? "Alles in Ordnung" : activeStatus === "warn" ? "Eingeschränkt" : "Störung"}
          </div>
        </div>

        {/* Refresh controls */}
        <div style={styles.refreshWrap}>
          <div style={styles.refreshCluster}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={styles.checkbox}
              />
              Automatisch aktualisieren
            </label>

            <div style={styles.intervalGroup}>
              <span style={styles.intervalLabel}>alle</span>
              <input
                type="number"
                min={2}
                max={60}
                value={refreshSec}
                onChange={(e) => setRefreshSec(clamp(Number(e.target.value || 5), 2, 60))}
                style={styles.intervalInput}
                disabled={!autoRefresh}
                aria-label="Abstand in Sekunden"
              />
              <span style={styles.intervalLabel}>Sek.</span>
            </div>
          </div>

          <button
            style={styles.button(true)}
            onClick={() => (view === "logs" ? loadLogs() : loadMetrics())}
            disabled={view === "logs" ? logsLoading : metricsLoading}
          >
            {(view === "logs" ? logsLoading : metricsLoading) ? "Lädt …" : "Jetzt aktualisieren"}
          </button>
        </div>
      </div>

      {view === "logs" ? (
        <LogsView
          styles={styles}
          services={services}
          servicesLoading={servicesLoading}
          service={service}
          setService={setService}
          minutes={minutes}
          setMinutes={setMinutes}
          limit={limit}
          setLimit={setLimit}
          query={query}
          setQuery={setQuery}
          rows={rows}
          logsLoading={logsLoading}
          logsErr={logsErr}
          selected={selected}
          setSelected={setSelected}
        />
      ) : (
        <PerformanceView
          styles={styles}
          metrics={metrics}
          metricsErr={metricsErr}
          windowMinutes={windowMinutes}
          setWindowMinutes={setWindowMinutes}
          history={metricsHistory}
        />
      )}
    </div>
  );
}
